import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Grid, Button, Dropdown, DropdownMenuItem} from 'views/components';
import {TimePicker, Translate, translate} from 'views/module';
import {Close} from 'views/icons';

import moment from 'moment';
import momentTZ from 'moment-timezone';

const Schedule = (props) => {
  const {setIsStepValid, newJob, updateJobSchedule} = props;

  // Get schedule types from translation object
  const scheduleTypesObject = translate('career.create.Schedule.ScheduleTypes');
  const scheduleTypes = Object.keys(scheduleTypesObject);

  // Get days of week from translation object
  const daysOfWeekObject = translate('career.create.Schedule.Day');
  const daysOfWeek = Object.keys(daysOfWeekObject);

  const scheduleType = newJob?.schedule?.type || 'RECURRENCE_EVENT';
  const scheduleData = newJob?.schedule?.schedule || {
    weeklySlots: [], // Array of {day, startTime, endTime} objects
    timezone: momentTZ.tz.guess(),
  };

  // Save data to job object whenever state changes
  React.useEffect(() => {
    const dataToSave = {
      type: scheduleType,
      schedule: scheduleType === 'LIVEIN_EVENT' ?
        {
          startDate: moment(),
          endDate: null,
          timezone: scheduleData.timezone,
          byweekday: daysOfWeek,
        } :
        scheduleData,
    };
    updateJobSchedule(() => dataToSave);
  }, [scheduleType, scheduleData, updateJobSchedule]);

  // Handle schedule type changes - set appropriate defaults
  React.useEffect(() => {
    if (scheduleType === 'LIVEIN_EVENT') {
      updateJobSchedule((prev) => ({
        ...prev,
        schedule: {
          ...prev.schedule,
          weeklySlots: [],
        },
      }));
    }
  }, [scheduleType, updateJobSchedule]);

  // Auto-load a default event when opening schedule for the first time
  React.useEffect(() => {
    if (scheduleType === 'RECURRENCE_EVENT' && scheduleData.weeklySlots.length === 0) {
      // Only auto-load if there are no existing slots and we're not loading from saved data
      if (!newJob?.schedule?.schedule?.weeklySlots?.length) {
        const defaultSlot = {
          day: 'MO', // Default to Monday
          startTime: moment().hour(9).minute(0).second(0),
          endTime: moment().hour(17).minute(0).second(0),
        };

        updateJobSchedule((prev) => ({
          ...prev,
          schedule: {
            ...prev.schedule,
            weeklySlots: [defaultSlot],
          },
        }));
      }
    }
  }, [scheduleType, newJob?.schedule?.schedule?.weeklySlots?.length, updateJobSchedule]);

  React.useEffect(() => {
    if (scheduleType === 'RECURRENCE_EVENT') {
      for (const slot of scheduleData.weeklySlots) {
        if (!slot.day || !slot.startTime || !slot.endTime) {
          setIsStepValid(false);
          return;
        }

        if (moment(slot.startTime).isSame(moment(slot.endTime))) {
          setIsStepValid(false);
          return;
        }
      }
    } else if (scheduleType === 'LIVEIN_EVENT') {
      const isGenerallyValid = scheduleData.timezone;
      if (!isGenerallyValid) {
        setIsStepValid(false);
        return;
      }
    }

    setIsStepValid(true);
  }, [setIsStepValid, scheduleType, scheduleData]);

  const addWeeklySlot = () => {
    const newSlot = {
      day: 'MO', // Default to Monday
      startTime: moment().hour(9).minute(0).second(0),
      endTime: moment().hour(17).minute(0).second(0),
    };

    updateJobSchedule((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        weeklySlots: [...(prev.schedule?.weeklySlots || []), newSlot],
      },
    }));
  };

  const removeWeeklySlot = (slotIndex) => {
    updateJobSchedule((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        weeklySlots: (prev.schedule?.weeklySlots || []).filter((_, index) => index !== slotIndex),
      },
    }));
  };

  const updateWeeklySlot = (slotIndex, field, value) => {
    updateJobSchedule((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        weeklySlots: (prev.schedule?.weeklySlots || []).map((slot, index) => {
          if (index !== slotIndex) return slot;

          const updatedSlot = {...slot, [field]: value};

          if (field === 'endTime' || field === 'startTime') {
            const startTime = field === 'startTime' ? value : slot.startTime;
            const endTime = field === 'endTime' ? value : slot.endTime;

            if (startTime && endTime) {
              const start = moment(startTime);
              const end = moment(endTime);

              const startTimeOfDay = start.hour() * 60 + start.minute();
              const endTimeOfDay = end.hour() * 60 + end.minute();

              // Set endTime to have the same date as startTime
              updatedSlot.endTime = start.clone().hour(end.hour()).minute(end.minute()).second(0);
              // If it's an overnight shift, add a day
              if (endTimeOfDay <= startTimeOfDay) {
                updatedSlot.endTime.add(1, 'day');
              }
            }
          }

          return updatedSlot;
        }),
      },
    }));
  };

  return (
    <Box sx={{p: 2}}>
      <Typography variant="h4" gutterBottom sx={{textAlign: 'center', mb: 2}}>
        <Translate expression="career.create.Schedule.Title" />
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom sx={{mb: 3, textAlign: 'center'}}>
        <Translate expression="career.create.Schedule.Description" />
      </Typography>

      {/* Schedule Type Selection */}
      <Box sx={{mb: 3}}>
        <Dropdown
          label={translate('career.create.Schedule.ScheduleTypeLabel')}
          value={scheduleType}
          onChange={(newType) => updateJobSchedule((prev) => ({
            ...prev,
            type: newType,
          }))}
        >
          {
            scheduleTypes.map((type) => (
              <DropdownMenuItem key={type} value={type}>
                <Translate expression={`career.create.Schedule.ScheduleType.${type}`} />
              </DropdownMenuItem>
            ))
          }
        </Dropdown>
      </Box>

      {/* Render appropriate schedule form */}
      {
        scheduleType === 'RECURRENCE_EVENT' &&
        <Grid container spacing={2}>
          {/* Weekly schedule slots */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              <Translate expression="career.create.Schedule.WeeklySchedule" />
            </Typography>

            {
              scheduleData.weeklySlots.map((slot, index) => (
                <Box key={index} sx={{mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1}}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <Dropdown
                        value={slot.day}
                        onChange={(value) => updateWeeklySlot(index, 'day', value)}
                        label={translate('career.create.Schedule.DayLabel')}
                        renderValue={(selected) => {
                          // Check if overnight by comparing dates
                          const isOvernight = moment(slot.endTime).isAfter(moment(slot.startTime), 'day');
                          return (
                            <>
                              <Translate expression={`career.create.Schedule.Day.${selected}`} />
                              {isOvernight && ' (overnight)'}
                            </>
                          );
                        }}
                      >
                        {
                          daysOfWeek.map((day) => (
                            <DropdownMenuItem key={day} value={day}>
                              <Translate expression={`career.create.Schedule.Day.${day}`} />
                            </DropdownMenuItem>
                          ))
                        }
                      </Dropdown>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TimePicker
                        label={translate('career.create.Schedule.StartTimeLabel')}
                        value={slot.startTime}
                        onChange={(time) => updateWeeklySlot(index, 'startTime', time)}
                        textFieldProps={{
                          fullWidth: true,
                          InputLabelProps: {shrink: true},
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TimePicker
                        label={translate('career.create.Schedule.EndTimeLabel')}
                        value={slot.endTime}
                        onChange={(time) => updateWeeklySlot(index, 'endTime', time)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => removeWeeklySlot(index)}
                        sx={{
                          minWidth: '40px',
                          height: '40px',
                          padding: '0',
                        }}
                      >
                        <Close />
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              ))
            }

            <Button
              variant="outlined"
              onClick={addWeeklySlot}
              sx={{mt: 1}}
            >
              <Translate expression="career.create.Schedule.AddTimeSlot" />
            </Button>
          </Grid>
        </Grid>
      }
      {
        scheduleType === 'LIVEIN_EVENT' &&
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" sx={{mt: 2, fontStyle: 'italic'}}>
              <Translate expression="career.create.Schedule.LiveInNote" />
            </Typography>
          </Grid>
        </Grid>
      }
    </Box>
  );
};

Schedule.propTypes = {
  setIsStepValid: PropTypes.func.isRequired,
  newJob: PropTypes.object.isRequired,
  updateJobSchedule: PropTypes.func.isRequired,
};

export default Schedule;
